/**
 * Lightweight Telegram WebApp wrapper.
 *
 * We avoid pulling in the official SDK at this layer — the global script
 * `telegram-web-app.js` is loaded in index.html and exposes `window.Telegram.WebApp`.
 * Outside Telegram (local dev in a browser) all methods become no-ops.
 */

type HapticImpactStyle = "light" | "medium" | "heavy" | "rigid" | "soft";
type HapticNotificationType = "error" | "success" | "warning";

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: {
    user?: {
      id: number;
      first_name?: string;
      last_name?: string;
      username?: string;
      language_code?: string;
      is_premium?: boolean;
      photo_url?: string;
    };
    start_param?: string;
  };
  colorScheme: "light" | "dark";
  themeParams: Record<string, string>;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;

  ready: () => void;
  expand: () => void;
  close: () => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;

  HapticFeedback: {
    impactOccurred: (style: HapticImpactStyle) => void;
    notificationOccurred: (type: HapticNotificationType) => void;
    selectionChanged: () => void;
  };

  MainButton: {
    text: string;
    color: string;
    textColor: string;
    isVisible: boolean;
    isActive: boolean;
    setText: (text: string) => void;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    show: () => void;
    hide: () => void;
    enable: () => void;
    disable: () => void;
    showProgress: (leaveActive?: boolean) => void;
    hideProgress: () => void;
  };

  BackButton: {
    isVisible: boolean;
    onClick: (cb: () => void) => void;
    offClick: (cb: () => void) => void;
    show: () => void;
    hide: () => void;
  };

  showAlert: (message: string, cb?: () => void) => void;
  showConfirm: (message: string, cb?: (ok: boolean) => void) => void;
  showPopup: (
    params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: string; text?: string }> },
    cb?: (buttonId: string) => void,
  ) => void;

  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  shareToStory?: (mediaUrl: string, params?: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

const noop = (): void => undefined;

export const tg: TelegramWebApp | null = typeof window !== "undefined" ? (window.Telegram?.WebApp ?? null) : null;

export function initTelegram(): void {
  if (!tg) return;
  tg.ready();
  tg.expand();
  tg.setHeaderColor("#0A0A0B");
  tg.setBackgroundColor("#0A0A0B");
}

/** Haptic feedback — falls back to no-op when running outside Telegram. */
export const haptic = {
  impact: (style: HapticImpactStyle = "light"): void => tg?.HapticFeedback.impactOccurred(style) ?? noop(),
  notify: (type: HapticNotificationType): void => tg?.HapticFeedback.notificationOccurred(type) ?? noop(),
  select: (): void => tg?.HapticFeedback.selectionChanged() ?? noop(),
};

/** Bot username derived from BotFather link — used for share URLs. */
export const BOT_USERNAME = "SilentPosterBot";
